/**
 * Transaction Cancellation Success Screen
 *
 * Confirmation screen shown after successful transaction cancellation
 * Shows success message and allows user to continue
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { useEffect } from "react";
import { RampsButton } from "@/components/ramps/Button";
import { useAnalytics } from "@/hooks/usePostHog";
import { useWebViewIntegration } from "@/lib/url-state";
import { useRampsStore } from "@/stores/use-ramps-store";
import { ANALYTICS_EVENTS } from "@/utils/analytics";
import { RampsText } from "../components/ramps/Text";

export const Route = createFileRoute("/ramps/cancel-success")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			wallet_provider?: string;
			lang?: string;
		},
	component: CancellationSuccessPage,
});

function CancellationSuccessPage() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const { sendToWebView } = useWebViewIntegration();
	const rampsState = useRampsStore();
	const { trackPageView, trackEvent, trackExit } = useAnalytics();

	// PostHog: page view and transaction_cancelled when user lands here (cancellation already committed)
	useEffect(() => {
		trackPageView("cancel_success", { page_name: "cancel_success" });
		trackEvent(ANALYTICS_EVENTS.PAGE_CANCEL_SUCCESS_VIEWED, {
			transaction_id: search.transaction_id || rampsState.transactionId,
			wallet_provider: search.wallet_provider,
		});
		// transaction_cancelled is sent via trackExit (eventMap in usePostHog)
		trackExit("cancelled", {
			exit_type: "cancelled",
			transaction_id: search.transaction_id || rampsState.transactionId,
			transaction_status: "cancelled",
			exit_reason: "user_cancelled_via_cancel_flow",
		});
	}, [
		trackPageView,
		trackEvent,
		trackExit,
		search.transaction_id,
		search.wallet_provider,
		rampsState.transactionId,
	]);

	const handleDone = () => {
		// Warn if sep24TransactionResponse is missing (v5 format)
		if (
			rampsState.commitResponse &&
			!rampsState.commitResponse.sep24TransactionResponse
		) {
			console.warn(
				"[Cancel Success] sep24TransactionResponse missing (v5 format), using fallback",
			);
		}

		// Prepare transaction response
		const transactionResponse = rampsState.commitResponse
			?.sep24TransactionResponse || {
			id: search.transaction_id || rampsState.transactionId,
			status: "cancelled",
			more_info_url: "",
			stellar_transaction_id: "",
			external_transaction_id:
				search.transaction_id || rampsState.transactionId,
		};

		// Send transaction in legacy format to parent window
		if (window.parent && window.parent !== window) {
			window.parent.postMessage(
				{
					transaction: transactionResponse,
				},
				"*",
			);
		}

		navigate({
			to: "/transaction-status",
			search: {
				transaction_id: search.transaction_id || "",
				token: search.token || "",
				lang: rampsState.lang || "en",
				// biome-ignore lint/suspicious/noExplicitAny: TanStack Router requires flexible search param type
			} as any,
		});

		// Also use the existing WebView integration for additional compatibility
		sendToWebView("TRANSACTION_CANCELLED", {
			transactionId: search.transaction_id || rampsState.transactionId,
			status: "cancelled",
			referenceNumber: rampsState.commitResponse?.referenceNumber || "",
			token: search.token || rampsState.token,
			message: "Transaction cancelled successfully",
			timestamp: new Date().toISOString(),
		});
	};

	return (
		<div
			className="flex min-h-dvh flex-col"
			style={{ backgroundColor: "var(--ramps-background-base)" }}
		>
			{/* Content */}
			<div className="flex flex-1 flex-col items-center justify-items-start px-4 pt-8 text-center">
				{/* Success Icon */}
				<div className="mb-8">
					<div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-ramps-status-bl">
						<Check
							size={32}
							style={{ color: "var(--ramps-text)" }}
							strokeWidth={1}
						/>
					</div>
				</div>

				{/* Success Message */}
				<div className="mb-8">
					<RampsText variant="title-lg-bold" color="primary" className="mb-4">
						Cancellation successful
					</RampsText>
					<RampsText
						variant="body-default"
						color="secondary"
						className="max-w-sm"
					>
						Your refund will be credited to your wallet shortly
					</RampsText>
				</div>
			</div>

			{/* Bottom Button */}
			<div className="p-4">
				<RampsButton
					variant="primary"
					size="large"
					className="w-full"
					onClick={handleDone}
				>
					Done
				</RampsButton>
			</div>
		</div>
	);
}
